import { CloudUpload } from 'lucide-react'
import { useCallback, useRef, useState, type DragEvent } from 'react'

import { cx } from '../../ui'

interface Props {
  onFiles: (files: File[]) => void
  disabled?: boolean
}

/**
 * Zona de selección de archivos.
 *
 * Arrastrar y soltar es el gesto natural en escritorio, pero **no existe en
 * móvil**: ahí toda la superficie es un botón que abre el selector nativo. Por
 * eso el contenedor entero es clicable y no sólo un enlace pequeño.
 */
export function UploadDropzone({ onFiles, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragging(false)

      if (disabled) return

      const files = [...event.dataTransfer.files]
      if (files.length > 0) onFiles(files)
    },
    [disabled, onFiles],
  )

  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          if (!disabled) inputRef.current?.click()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={cx(
        'flex w-full flex-col items-center gap-3 rounded-card border-2 border-dashed p-6 text-center transition-colors sm:p-10',
        // `bg-accent/5` funciona ahora que los tokens van en canales sueltos:
        // antes, sobre una variable con el color completo, el modificador de
        // opacidad de Tailwind no podía operar.
        dragging ? 'border-accent bg-accent/5' : 'border-line-strong',
        disabled === true ? 'opacity-50' : 'cursor-pointer hover:border-accent',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])]
          if (files.length > 0) onFiles(files)
          // Permite volver a elegir el mismo archivo tras un fallo.
          event.target.value = ''
        }}
      />

      <CloudUpload
        size={26}
        strokeWidth={1.5}
        aria-hidden
        className={dragging ? 'text-accent' : 'text-ink-subtle'}
      />

      <div className="flex flex-col gap-1">
        <p className="text-subhead font-medium text-ink">
          {/* El texto de arrastrar sólo aparece donde el gesto existe. */}
          <span className="hidden sm:inline">Arrastra tus archivos o haz clic para elegirlos</span>
          <span className="sm:hidden">Toca para elegir archivos</span>
        </p>
        <p className="text-body text-ink-muted">
          Suben directos al almacenamiento, sin pasar por nuestros servidores. Si
          se corta internet, retoman donde iban.
        </p>
      </div>
    </div>
  )
}
